import { SiteUrls } from '@tableau/shared-urls';
import * as React from 'react';
import { defaultMemoize } from 'reselect';

import { BrowserTitleService } from '~commonInfra/services/BrowserTitleService';
import { ISanitizer } from '~commonInfra/utils/Sanitize';
import { Messages } from '~src/codegen/Localize';
import {
  PredictiveModelOverviewSubspace
} from '~src/predictiveModel/components/PredictiveModelOverviewSubspace';
import { PredictiveModelSpaceHeader } from '~src/predictiveModel/components/PredictiveModelSpaceHeader';
import { IPredictiveModelActions } from '~src/predictiveModel/serverApi/types/IPredictiveModelActions';
import { PredictiveModelSubspaceProps } from '~src/predictiveModel/types/PredictiveModelSubspaceProps';
import _ = require('underscore');

import {
  IPredictiveModelDetailInfo,
  IPredictiveModelInfo,
  IPredictiveModelInfoWithUsage
} from '~src/predictiveModel/models/IPredictiveModelInfo';
import { ContentSpaceInfo } from '~src/spacesPlatform/actions/EnterSpaceAction';
import {
  addContentTypeToProjectHierarchy, populateBreadcrumbsForPS,
  populateContentBreadcrumbs
} from '~src/spacesPlatform/components/breadcrumbs/BreadcrumbHelpers';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { Subspace, TabbedSpace } from '~src/spacesPlatform/components/spaceComponents/TabbedSpace';
import { INamedItemInfo } from '~src/spacesPlatform/models/resourceInfoParts';
import { PredictiveModelSubspaceName } from '~src/spacesPlatform/state/SpaceInfo';

export type PredictiveModelViewerSpaceOwnProps = Readonly<{
  readonly predictiveModelAllowedActions: IPredictiveModelActions;
  readonly predictiveModelDetailInfo: IPredictiveModelInfoWithUsage;
  readonly predictiveModelInfo: IPredictiveModelInfo;
  readonly projectHierarchy: INamedItemInfo[];

  readonly refreshData: () => void;

  // subspace info
  readonly subspaceName: PredictiveModelSubspaceName;
  readonly siteUrls: SiteUrls;

  readonly sanitizer: ISanitizer;
}>;

export type PredictiveModelViewerSpaceStateProps = Readonly<{
  readonly urls: SpacesSiteUrls;
  readonly catalogingOnSiteEnabled: boolean;
}>;

export type PredictiveModelViewerSpaceDispatchProps = Readonly<{
  readonly enterSpace: (predictiveModelDetailInfo?: IPredictiveModelDetailInfo) => void;
  readonly addToFavorites: () => void;
  readonly removeFromFavorites: () => void;
  readonly requestSpaceContents: () => void;
  readonly enterContentSpace: (contentSpaceInfo: ContentSpaceInfo) => void;
}>;

export type PredictiveModelViewerSpaceProps = PredictiveModelViewerSpaceStateProps
  & PredictiveModelViewerSpaceDispatchProps
  & PredictiveModelViewerSpaceOwnProps;

// TODO: Add columnCount + dataUsed as fields once they are retrievable from a predictive model version.
function overviewSubspace(subspaceProps: PredictiveModelSubspaceProps): Subspace {
  const predictiveModel = subspaceProps.predictiveModelDetailInfo;
  const activeVersion = predictiveModel?.activeVersion;
  const deployedAt = activeVersion?.deployedAt;
  const latestDeployedAt = deployedAt && deployedAt.length > 0 ? new Date(Math.max.apply(undefined, deployedAt)) : undefined;
  const deployedVersion = deployedAt ? activeVersion?.versionNumber : undefined;
  return {
    name: 'overview',
    tabLabel: Messages.overview(),
    renderContent: () => <PredictiveModelOverviewSubspace
      modelName={predictiveModel?.name}
      description={activeVersion?.description}
      goal={activeVersion?.objectiveGoal}
      modelLastModified={activeVersion?.updatedAt}
      modelLastTrained={activeVersion?.trainedAt}
      modelLastDeployed={latestDeployedAt}
      deployedVersion={deployedVersion}
      algorithm={activeVersion?.objectiveAlgorithm}
      modelType={activeVersion?.objectiveTargetFieldType}
      objectiveSuccessValue={activeVersion?.objectiveSuccessValue}
      objectiveTargetField={activeVersion?.objectiveTargetField}
      outcomeField={activeVersion?.objectiveTargetField}
    />,
    shouldRenderTabContentCount: false
  };
}

function getPredictiveModelSubspaces(
  subspaceProps: PredictiveModelSubspaceProps
): ReadonlyArray<Subspace> {
  // TODO: Add the remaining subspaces after alpha
  return _.compact([
    overviewSubspace(subspaceProps)
  ]);
}

function getPredictiveModelDetailWithUsage(
  predictiveModelDetailInfo: IPredictiveModelInfoWithUsage,
  predictiveModelInfo: IPredictiveModelInfo,
) {
  return {
    usageInfo: predictiveModelInfo.usageInfo,
    usageInfoMaxValues: predictiveModelInfo.usageInfoMaxValues,
    ...predictiveModelDetailInfo,
  };
}

export class PredictiveModelViewerSpace extends React.PureComponent<PredictiveModelViewerSpaceProps> {
  private memoizedGetPredictiveModelSubspaces = defaultMemoize(getPredictiveModelSubspaces);
  private memoizedPredictiveModelDetailWithUsage = defaultMemoize(getPredictiveModelDetailWithUsage);

  constructor(props: PredictiveModelViewerSpaceProps) {
    super(props);
    props.enterSpace();
    props.enterContentSpace(
      {
        item: props.predictiveModelDetailInfo,
        spaceType: 'predictiveModel',
        subspaceName: props.subspaceName,
        contentLocation: props.predictiveModelDetailInfo.container.type,
        ownerId: props.predictiveModelDetailInfo.owner.id
      }
    );
  }

  public urlFromSubspaceName = (subspaceName?: PredictiveModelSubspaceName)  => {
    switch (subspaceName) {
      case 'overview':
        return this.props.urls.predictiveModelOverviewUrl(this.props.predictiveModelInfo.id);
      case 'connections':
        return this.props.urls.predictiveModelConnectionsUrl(this.props.predictiveModelInfo.id);
      case 'connectedContent':
        return this.props.urls.predictiveModelConnectedContentUrl(this.props.predictiveModelInfo.id);
    }
    return this.props.urls.predictiveModelUrl(this.props.predictiveModelInfo.id);
  };

  public componentDidMount() {
    BrowserTitleService.setTitleWithPlaceEntityName({ placeEntityName: 'predictive model' });
    this.props.requestSpaceContents();
    this.updateBreadcrumbs();
  }

  public componentDidUpdate(prevProps: PredictiveModelViewerSpaceProps) {
    if (this.props.predictiveModelDetailInfo !== prevProps.predictiveModelDetailInfo ||
      this.props.subspaceName !== prevProps.subspaceName) {
      this.props.enterSpace();
      this.updateBreadcrumbs();
    }
  }

  public render(): JSX.Element {

    const subspaceProps: PredictiveModelSubspaceProps = {
      enterSpace: this.props.enterSpace,
      refreshData: this.props.refreshData,
      siteUrls: this.props.siteUrls,
      sanitizer: this.props.sanitizer,
      predictiveModelDetailInfo: this.props.predictiveModelDetailInfo,
    };

    const subspaces = this.memoizedGetPredictiveModelSubspaces(subspaceProps);

    return (
      <TabbedSpace
        subspaces={subspaces}
        activeSubspaceName={this.props.subspaceName as PredictiveModelSubspaceName}
        urlForSubspace={this.urlFromSubspaceName}
        mastheadElement={this.mastheadElement()}
        testIdPrefix={'predictiveModel'}
        spaceItemName={this.props.predictiveModelDetailInfo.name}
      />
    );
  }

  private updateBreadcrumbs() {
    const projectHierarchyWithType = addContentTypeToProjectHierarchy(this.props.projectHierarchy);
    if (this.props.projectHierarchy.length === 0) {
      populateBreadcrumbsForPS([this.props.predictiveModelDetailInfo], this.props.urls);
    } else {
      populateContentBreadcrumbs([...projectHierarchyWithType, this.props.predictiveModelDetailInfo], this.props.urls);
    }
  }

  private mastheadElement(): React.ReactNode {
    const predictiveModelDetailWithUsage = this.memoizedPredictiveModelDetailWithUsage(
      this.props.predictiveModelDetailInfo,
      this.props.predictiveModelInfo,
    );

    return <PredictiveModelSpaceHeader
      itemInfo={predictiveModelDetailWithUsage}
      availableActions={this.props.predictiveModelAllowedActions}
      addToFavorites={this.props.addToFavorites}
      removeFromFavorites={this.props.removeFromFavorites}
      urls={this.props.urls}
      catalogingOnSiteEnabled={this.props.catalogingOnSiteEnabled}
    />;
  }
}
